import React from 'react';
import { formatLapTime, formatDate, formatSpeed } from '../utils/format';
import { getTimezone, formatTimeAtTrack } from '../utils/timezone';
import { WeatherIcon, formatTemp } from '../utils/weather';
import { SessionTypeIcon } from '../utils/RaceImageMapper';
import TrackThumbnail from './TrackThumbnail';

const styles = {
  card: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 10,
    padding: '20px',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 8,
  },
  trackName: {
    fontFamily: 'var(--font-heading)',
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--color-text)',
    letterSpacing: 0.5,
  },
  sessionTypeBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 700,
    fontFamily: 'var(--font-heading)',
    letterSpacing: 1,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    color: '#0d6efd',
    background: 'rgba(13, 110, 253, 0.12)',
  },
  detail: {
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    color: 'var(--color-text-dim)',
    marginBottom: 4,
  },
  bestLap: {
    fontFamily: 'var(--font-data)',
    fontSize: 18,
    fontWeight: 500,
    color: 'var(--color-accent)',
    marginTop: 10,
  },
  lapLabel: {
    fontFamily: 'var(--font-heading)',
    fontSize: 9,
    color: 'var(--color-text-dim)',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    marginTop: 12,
  },
  exampleBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 10,
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    background: 'rgba(243, 156, 18, 0.15)',
    color: '#f39c12',
    marginBottom: 8,
  },
};

export function getSessionDateLabel(session, tracks) {
  if (!session?.date) return 'Unknown Day';
  let label = formatDate(session.date);
  if (session.createdAt && tracks) {
    const track = tracks.find((t) => t.trackId === session.trackId);
    const loc = track?.location;
    if (loc) {
      const tz = getTimezone(loc.lat, loc.lng);
      const timeStr = formatTimeAtTrack(session.createdAt, tz);
      if (timeStr) label += ` ${timeStr}`;
    }
  }
  return label;
}

export function formatSessionTypeLabel(sessionType) {
  const value = String(sessionType || '').trim();
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

export function getSessionTypeColor(sessionType) {
  const normalized = String(sessionType || '').toLowerCase();
  if (normalized === 'demo') return '#9ca3af';
  if (normalized === 'race') return '#86efac';
  return 'var(--color-border)';
}

export function getSessionTypeBadgeStyle(sessionType) {
  const normalized = String(sessionType || '').toLowerCase();
  if (normalized === 'demo') {
    return {
      color: '#4b5563',
      background: 'rgba(156, 163, 175, 0.22)',
    };
  }
  if (normalized === 'race') {
    return {
      color: '#166534',
      background: 'rgba(134, 239, 172, 0.28)',
    };
  }
  return {
    color: '#0d6efd',
    background: 'rgba(13, 110, 253, 0.12)',
  };
}

const SESSION_TYPE_OPTIONS = ['Practice', 'Qualifying', 'Race'];

export default function SessionTile({ session, tracks, trackThumbMap, onClick, badge, onSessionTypeChange, onTrackChange, trackOptions, onDelete }) {
  return (
    <div
      style={styles.card}
      data-doc-id="session-card"
      data-track-id={session.trackId || ''}
      data-session-type={(session.sessionType || '').toLowerCase()}
      onClick={onClick}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--color-accent)')}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--color-border)')}
    >
      {badge && (
        <div style={{
          ...styles.sessionTypeBadge,
          ...badge.style,
          marginBottom: 6,
          alignSelf: 'flex-start',
        }}>
          {badge.label}
        </div>
      )}
      {session.isExample && (
        <div style={styles.exampleBadge}>Example Data</div>
      )}
      <div style={styles.cardHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
          {session.trackId && trackThumbMap?.[session.trackId] && (
            <TrackThumbnail
              trackId={session.trackId}
              alt=""
              style={{ width: 56, height: 40, borderRadius: 4, flexShrink: 0 }}
            />
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            {onTrackChange && trackOptions ? (
              <select
                value={session.trackId || ''}
                onChange={(e) => { e.stopPropagation(); onTrackChange(e.target.value); }}
                onClick={(e) => e.stopPropagation()}
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: 14,
                  fontWeight: 700,
                  color: 'var(--color-text)',
                  letterSpacing: 0.5,
                  background: 'var(--color-surface-light)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 4,
                  padding: '4px 8px',
                  cursor: 'pointer',
                  width: '100%',
                  maxWidth: 300,
                }}
              >
                <option value="" style={{ background: 'var(--color-surface-light)', color: 'var(--color-text-dim)' }}>Select track...</option>
                {trackOptions.map((t) => (
                  <option key={t.trackId} value={t.trackId} style={{ background: 'var(--color-surface-light)', color: 'var(--color-text)' }}>{t.name}</option>
                ))}
              </select>
            ) : (
              <div style={styles.trackName}>{session.track}</div>
            )}
            <div style={styles.detail}>{getSessionDateLabel(session, tracks)}</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
          {onSessionTypeChange ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
            <span style={{ fontSize: 10, color: 'var(--color-text-dim)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Session Type</span>
            <select
              value={formatSessionTypeLabel(session.sessionType) || ''}
              onChange={(e) => { e.stopPropagation(); onSessionTypeChange(e.target.value); }}
              onClick={(e) => e.stopPropagation()}
              style={{
                ...styles.sessionTypeBadge,
                background: 'var(--color-surface-light)',
                color: 'var(--color-text)',
                border: '1px solid var(--color-border)',
                outline: 'none',
                cursor: 'pointer',
                appearance: 'none',
                WebkitAppearance: 'none',
                paddingRight: 18,
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5' viewBox='0 0 8 5'%3E%3Cpath d='M0 0l4 5 4-5z' fill='%23888'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 6px center',
              }}
            >
              <option value="" style={{ background: 'var(--color-surface-light)', color: 'var(--color-text-dim)' }}>Select...</option>
              {SESSION_TYPE_OPTIONS.map((opt) => (
                <option key={opt} value={opt} style={{ background: 'var(--color-surface-light)', color: 'var(--color-text)' }}>{opt}</option>
              ))}
            </select>
            </div>
          ) : (
            <SessionTypeIcon
              sessionType={session.sessionType}
              size={14}
              style={{
                fontFamily: 'var(--font-heading)',
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: 'uppercase',
                color: 'var(--color-text-dim)',
              }}
            />
          )}
          {session.deviceType && session.deviceType.toLowerCase() !== 'unknown' && (
            <div
              style={{
                ...styles.sessionTypeBadge,
                color: '#7c6f64',
                background: 'rgba(180, 160, 130, 0.18)',
              }}
            >
              {session.deviceType}
            </div>
          )}
        </div>
      </div>
      {session.weather && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2, marginTop: 2 }}>
          <WeatherIcon condition={session.weather.condition} size={16} />
          <span style={{ fontFamily: 'var(--font-data)', fontSize: 12, color: 'var(--color-text)' }}>
            {formatTemp(session.weather.temperature)}
          </span>
          {session.weather.humidity != null && (
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--color-text-dim)' }}>
              {session.weather.humidity}%
            </span>
          )}
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--color-text-dim)' }}>
            {session.weather.label}
          </span>
          {session.weather.windSpeed != null && (
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--color-text-dim)' }}>
              {session.weather.windSpeed} mph
            </span>
          )}
        </div>
      )}
      <div style={styles.detail}>{session.car}</div>
      <div style={styles.detail}>{session.lapCount} laps</div>
      <div style={styles.detail}>{formatSpeed(session.maxSpeed || 0)} top speed</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <div style={styles.lapLabel}>Best Lap</div>
          <div style={styles.bestLap}>{formatLapTime(session.bestLap)}</div>
        </div>
        {onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(session); }}
            title="Delete session"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 6,
              borderRadius: 4,
              color: 'var(--color-text-dim)',
              opacity: 0.4,
              transition: 'opacity 0.2s, color 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = '#e74c3c'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.4'; e.currentTarget.style.color = 'var(--color-text-dim)'; }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 4h12M5.33 4V2.67a1.33 1.33 0 011.34-1.34h2.66a1.33 1.33 0 011.34 1.34V4M12.67 4v9.33a1.33 1.33 0 01-1.34 1.34H4.67a1.33 1.33 0 01-1.34-1.34V4h9.34z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
